import { useOpenAiGlobal } from "../use-openai-global";
import { useState, useRef, useCallback, useEffect as useReactEffect } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

type Activity = {
  id: string;
  name: string;
  mapLink?: string;
  image?: string;
  rating?: { score?: number; reviews?: number };
  location?: string;
  businessStatus?: string;
  icon?: string;
  openingHours?: { openNow?: boolean } | null;
  types?: string[];
  lat?: number;
  lng?: number;
};

const PLACE_TYPE_LABELS: Record<string, string> = {
  casino: "Casino",
  bar: "Bar",
  night_club: "Night Club",
  stadium: "Stadium",
  restaurant: "Restaurant",
  train_station: "Train Station",
  bus_station: "Bus Station",
  grocery_or_supermarket: "Supermarket",
  amusement_park: "Amusement Park",
  florist: "Florist",
  subway_station: "Subway Station",
  light_rail_station: "Light Rail",
  hair_care: "Hair Care",
  museum: "Museum",
  laundry: "Laundry",
  shopping_mall: "Shopping Mall",
  bakery: "Bakery",
  city_hall: "City Hall",
  airport: "Airport",
  park: "Park",
  hospital: "Hospital",
  pharmacy: "Pharmacy",
  police: "Police",
  post_office: "Post Office",
  library: "Library",
  school: "School",
  university: "University",
  church: "Church",
  mosque: "Mosque",
  synagogue: "Synagogue",
  hindu_temple: "Hindu Temple",
  tourist_attraction: "Tourist Attraction",
  art_gallery: "Art Gallery",
  aquarium: "Aquarium",
  zoo: "Zoo",
  spa: "Spa",
  gym: "Gym",
  movie_theater: "Movie Theater",
  bowling_alley: "Bowling Alley",
  cafe: "Cafe",
  lodging: "Lodging",
  store: "Store",
  clothing_store: "Clothing Store",
  book_store: "Book Store",
  jewelry_store: "Jewelry Store",
  shoe_store: "Shoe Store",
  electronics_store: "Electronics Store",
  furniture_store: "Furniture Store",
  home_goods_store: "Home Goods Store",
  hardware_store: "Hardware Store",
  bicycle_store: "Bicycle Store",
  car_dealer: "Car Dealer",
  car_rental: "Car Rental",
  car_repair: "Car Repair",
  car_wash: "Car Wash",
  gas_station: "Gas Station",
  parking: "Parking",
  taxi_stand: "Taxi Stand",
  transit_station: "Transit Station",
  atm: "ATM",
  bank: "Bank",
  dentist: "Dentist",
  doctor: "Doctor",
  veterinary_care: "Veterinary Care",
  beauty_salon: "Beauty Salon",
  establishment: "Establishment",
  point_of_interest: "Point of Interest",
};

const PLACE_TYPE_EMOJIS: Record<string, string> = {
  casino: "🎰",
  bar: "🍺",
  night_club: "🎉",
  stadium: "🏟️",
  restaurant: "🍽️",
  train_station: "🚂",
  bus_station: "🚌",
  grocery_or_supermarket: "🛒",
  amusement_park: "🎢",
  florist: "💐",
  subway_station: "🚇",
  light_rail_station: "🚊",
  hair_care: "💇",
  museum: "🏛️",
  laundry: "🧺",
  shopping_mall: "🛍️",
  bakery: "🥖",
  city_hall: "🏛️",
  airport: "✈️",
  park: "🌳",
  hospital: "🏥",
  pharmacy: "💊",
  police: "👮",
  post_office: "📮",
  library: "📚",
  school: "🏫",
  university: "🎓",
  church: "⛪",
  mosque: "🕌",
  synagogue: "🕍",
  hindu_temple: "🛕",
  tourist_attraction: "🗼",
  art_gallery: "🖼️",
  aquarium: "🐠",
  zoo: "🦁",
  spa: "💆",
  gym: "💪",
  movie_theater: "🎬",
  bowling_alley: "🎳",
  cafe: "☕",
  lodging: "🛏️",
  store: "🏪",
  clothing_store: "👔",
  book_store: "📖",
  jewelry_store: "💎",
  shoe_store: "👞",
  electronics_store: "📱",
  furniture_store: "🛋️",
  home_goods_store: "🏠",
  hardware_store: "🔨",
  bicycle_store: "🚲",
  car_dealer: "🚗",
  car_rental: "🚙",
  car_repair: "🔧",
  car_wash: "🧼",
  gas_station: "⛽",
  parking: "🅿️",
  taxi_stand: "🚕",
  transit_station: "🚉",
  atm: "🏧",
  bank: "🏦",
  dentist: "🦷",
  doctor: "👨‍⚕️",
  veterinary_care: "🐾",
  beauty_salon: "💅",
  establishment: "📍",
  point_of_interest: "⭐",
};

// Hook to calculate card width based on container
function useContainerCardWidth(containerRef: React.RefObject<HTMLDivElement | null>) {
  const [cardWidth, setCardWidth] = useState(280);

  useReactEffect(() => {
    const updateCardWidth = () => {
      if (!containerRef.current) return;
      const containerWidth = containerRef.current.offsetWidth;
      const newCardWidth = Math.max(280, Math.min(360, containerWidth - 48));
      setCardWidth(newCardWidth);
    };

    updateCardWidth();
    window.addEventListener("resize", updateCardWidth);
    return () => window.removeEventListener("resize", updateCardWidth);
  }, [containerRef]);

  return cardWidth;
}

export default function NearbyPlaces() {
  console.log("=== NearbyPlaces Component Rendering ===");
  
  const data = useOpenAiGlobal("toolOutput") as any;
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const [currentIndex, setCurrentIndex] = useState(0);
  const [viewMode, setViewMode] = useState<"carousel" | "map">("carousel");
  const containerRef = useRef<HTMLDivElement>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const cardWidth = useContainerCardWidth(containerRef);
  const cardWidthWithGap = cardWidth + 24;

  console.log("1. Raw toolOutput data:", data);
  console.log("2. Data type:", typeof data);
  console.log("3. Data keys:", data ? Object.keys(data) : "null");
  console.log("4. Activities array:", data?.activities);
  console.log("5. Activities length:", data?.activities?.length);

  // Extract and transform activities from MCP response
  const rawActivities = data?.activities || [];
  const activities: Activity[] = rawActivities.map((activity: any) => ({
    id: activity.id,
    name: activity.name,
    image: activity.photo || activity.image,
    mapLink: activity.url || activity.mapLink,
    rating: activity.rating != null ? {
      score: typeof activity.rating === 'number' ? activity.rating : activity.rating?.score,
      reviews: activity.reviewCount || activity.rating?.reviews
    } : undefined,
    location: activity.description || activity.location,
    businessStatus: activity.businessStatus,
    icon: activity.icon,
    openingHours: activity.openingHours,
    types: activity.types,
    lat: activity.location?.lat || activity.lat || activity.latitude,
    lng: activity.location?.lng || activity.lng || activity.longitude
  }));
  const searchType = data?.searchParams?.type || "places";
  const typeLabel = PLACE_TYPE_LABELS[searchType] || searchType;

  console.log("6. Extracted activities count:", activities.length);
  console.log("7. Type label:", typeLabel);
  console.log("8. First activity image URL:", activities[0]?.image);
  console.log("9. First activity mapLink:", activities[0]?.mapLink);

  const scrollToIndex = useCallback(
    (index: number) => {
      if (!containerRef.current) return;
      const scrollLeft = index * cardWidthWithGap;
      containerRef.current.scrollTo({ left: scrollLeft, behavior: "smooth" });
      setCurrentIndex(index);
    },
    [cardWidthWithGap],
  );

  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;
    const scrollLeft = containerRef.current.scrollLeft;
    const newIndex = Math.round(scrollLeft / cardWidthWithGap);
    setCurrentIndex(newIndex);
  }, [cardWidthWithGap]);

  // Initialize map when switching to map view
  useReactEffect(() => {
    if (viewMode !== "map" || !mapContainerRef.current || activities.length === 0) return;

    console.log("🗺️ Map view activated");
    console.log("Total activities:", activities.length);
    console.log("First activity:", activities[0]);
    console.log("First activity lat/lng:", activities[0]?.lat, activities[0]?.lng);

    // Clean up existing map
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }

    // Filter activities with valid coordinates
    const placesWithCoords = activities.filter(a => a.lat != null && a.lng != null);
    console.log("Places with coordinates:", placesWithCoords.length);
    
    if (placesWithCoords.length === 0) {
      console.warn("⚠️ No places have coordinates - cannot show map");
      return;
    }

    // Get hotel location from search params
    const hotelLat = data?.searchParams?.hotelLat || data?.searchParams?.lat;
    const hotelLng = data?.searchParams?.hotelLng || data?.searchParams?.lng;
    const hotelName = data?.searchParams?.hotelName || "Hotel";

    // Calculate center
    const allLats = placesWithCoords.map(a => a.lat!);
    const allLngs = placesWithCoords.map(a => a.lng!);
    if (hotelLat != null && hotelLng != null) {
      allLats.push(hotelLat);
      allLngs.push(hotelLng);
    }

    const centerLat = allLats.reduce((sum, lat) => sum + lat, 0) / allLats.length;
    const centerLng = allLngs.reduce((sum, lng) => sum + lng, 0) / allLngs.length;

    // Delay map initialization
    const timeoutId = setTimeout(() => {
      if (!mapContainerRef.current) return;

      const map = L.map(mapContainerRef.current, {
        center: [centerLat, centerLng],
        zoom: 13,
        scrollWheelZoom: true,
      });

      L.tileLayer("https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png", {
        attribution: '&copy; OpenStreetMap',
        maxZoom: 19,
      }).addTo(map);

      // Add hotel marker
      if (hotelLat != null && hotelLng != null) {
        const hotelIcon = L.divIcon({
          html: `<div style="background: white; width: 44px; height: 44px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 26px; box-shadow: 0 3px 10px rgba(0,0,0,0.3); border: 3px solid #000;">🏨</div>`,
          className: '',
          iconSize: [44, 44],
          iconAnchor: [22, 22],
        });
        const hotelMarker = L.marker([hotelLat, hotelLng], { icon: hotelIcon }).addTo(map);
        hotelMarker.bindPopup(`
          <div style="min-width: 200px; padding: 4px;">
            <h3 style="font-weight: bold; margin: 0 0 8px 0; font-size: 16px; color: #1f2937;">🏨 ${hotelName}</h3>
            <div style="color: #6b7280; font-size: 13px;">Your Hotel</div>
          </div>
        `);
      }

      // Add place markers
      placesWithCoords.forEach((activity) => {
        // Get emoji for this place type
        const placeType = data?.searchParams?.type || activity.types?.[0] || 'point_of_interest';
        const emoji = PLACE_TYPE_EMOJIS[placeType] || '📍';
        
        const markerIcon = L.divIcon({
          html: `<div style="background: white; width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 24px; box-shadow: 0 3px 10px rgba(0,0,0,0.3); border: 3px solid #000;">${emoji}</div>`,
          className: '',
          iconSize: [40, 40],
          iconAnchor: [20, 20],
        });

        const marker = L.marker([activity.lat!, activity.lng!], { icon: markerIcon }).addTo(map);
        
        const popupContent = `
          <div style="min-width: 260px; max-width: 300px;">
            ${activity.image ? `<img src="${activity.image}" crossorigin="anonymous" style="width: 100%; height: 150px; object-fit: cover; border-radius: 8px 8px 0 0; display: block; background: #f3f4f6;" alt="${activity.name}" onerror="this.style.display='none'" />` : ''}
            <div style="padding: 12px;">
              <h3 style="font-weight: bold; margin: 0 0 8px 0; font-size: 16px; color: #000;">${activity.name}</h3>
              ${activity.rating?.score ? `<div style="margin-bottom: 6px; color: #000; font-weight: 600;">⭐ ${activity.rating.score.toFixed(1)} ${activity.rating.reviews ? `<span style="color: #666; font-weight: normal;">(${activity.rating.reviews.toLocaleString()} reviews)</span>` : ''}</div>` : ''}
              ${activity.location ? `<div style="color: #666; font-size: 13px;">📍 ${activity.location}</div>` : ''}
            </div>
          </div>
        `;
        
        marker.bindPopup(popupContent);
      });

      // Fit bounds
      const bounds = L.latLngBounds(placesWithCoords.map(a => [a.lat!, a.lng!]));
      if (hotelLat != null && hotelLng != null) {
        bounds.extend([hotelLat, hotelLng]);
      }
      map.fitBounds(bounds, { padding: [50, 50] });

      mapInstanceRef.current = map;
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [viewMode, activities, data]);

  if (activities.length === 0) {
    return (
      <div className="min-h-screen bg-white rounded-2xl overflow-hidden border border-black/10 flex items-center justify-center p-4 sm:p-8">
        <div className="text-center bg-white border-2 border-black rounded-2xl shadow-xl flex flex-col items-center gap-4 sm:gap-6 p-6 sm:p-12 max-w-md w-full">
          <div className="w-20 h-20 rounded-full bg-black flex items-center justify-center">
            <span className="text-4xl text-white">�</span>
          </div>
          <div>
            <h3 className="font-bold text-black text-lg mb-2">No Places Found</h3>
            <p className="text-gray-600 text-sm">Ask about nearby places to see recommendations.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white dark:bg-gray-900 rounded-2xl overflow-hidden border border-black/10 dark:border-white/10">
      <header className="relative z-10 bg-black text-white border-b border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            <h1 className="text-lg sm:text-2xl font-black tracking-tight">
              Nearby {typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1)}
            </h1>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setViewMode(viewMode === "carousel" ? "map" : "carousel")}
                className="px-3 py-1.5 sm:px-4 sm:py-2 bg-white/10 hover:bg-white/20 text-white text-xs sm:text-sm font-semibold rounded-lg transition-all duration-200 flex items-center gap-2"
              >
                {viewMode === "carousel" ? (
                  <>
                    <span>🗺️</span>
                    <span className="hidden sm:inline">Map View</span>
                  </>
                ) : (
                  <>
                    <span>📋</span>
                    <span className="hidden sm:inline">List View</span>
                  </>
                )}
              </button>
              <span className="text-[10px] sm:text-xs text-white/50 font-medium tracking-wide whitespace-nowrap flex items-center gap-1">
                Powered by
                <span className="text-white font-bold">Quinta</span>
              </span>
            </div>
          </div>
          <p className="text-white text-sm sm:text-base font-semibold mt-1">
            {activities.length} place{activities.length !== 1 ? "s" : ""} found
          </p>
          <p className="text-white/60 text-[10px] sm:text-xs mt-2">
            Information from Google Maps. Verify details before visiting.
          </p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-3 sm:px-8 py-6 sm:py-12 dark:text-white">
        {viewMode === "map" ? (
          activities.filter(a => a.lat != null && a.lng != null).length === 0 ? (
            <div className="bg-white rounded-2xl border-2 border-black p-8 text-center">
              <div className="text-6xl mb-4">🗺️</div>
              <h3 className="text-xl font-bold mb-2">Map Not Available</h3>
              <p className="text-gray-600">Location coordinates are not available for these places. Use the "View on Map" button on each card to see them individually on Google Maps.</p>
              <button
                onClick={() => setViewMode("carousel")}
                className="mt-4 px-6 py-2 bg-black text-white font-semibold rounded-lg hover:bg-gray-800"
              >
                Back to List
              </button>
            </div>
          ) : (
            <div className="relative">
              <div 
                ref={mapContainerRef} 
                className="w-full h-[500px] sm:h-[600px] rounded-2xl overflow-hidden border-2 border-black shadow-xl"
              />
            </div>
          )
        ) : (
          <div className="relative">
          <button
            className="hidden sm:flex absolute -left-1 sm:left-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/80 backdrop-blur-md border border-gray-600 text-white hover:bg-black hover:shadow-lg transition-all duration-300 items-center justify-center disabled:opacity-40"
            onClick={() => scrollToIndex(Math.max(0, currentIndex - 1))}
            disabled={currentIndex === 0}
            aria-label="Previous place"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            className="hidden sm:flex absolute -right-1 sm:right-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/80 backdrop-blur-md border border-gray-600 text-white hover:bg-black hover:shadow-lg transition-all duration-300 items-center justify-center disabled:opacity-40"
            onClick={() => scrollToIndex(Math.min(activities.length - 1, currentIndex + 1))}
            disabled={currentIndex === activities.length - 1}
            aria-label="Next place"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <div
            ref={containerRef}
            onScroll={handleScroll}
            className="flex gap-4 sm:gap-6 overflow-x-auto scroll-smooth scrollbar-hide pb-4 sm:pb-6 snap-x snap-mandatory sm:snap-none"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
          {activities.map((activity, index) => {
            // Safely extract primary type as string
            const firstType = activity.types?.[0];
            const primaryType = typeof firstType === 'string' 
              ? (PLACE_TYPE_LABELS[firstType] || firstType) 
              : "";
            
            const isOpen = activity.openingHours?.openNow;
            const hasOpenInfo = activity.openingHours !== null && activity.openingHours !== undefined;
            const isOperational = !activity.businessStatus || activity.businessStatus === "OPERATIONAL";
            const hasRating = activity.rating?.score != null && activity.rating.score > 0;
            
            // Ensure all text values are strings
            const safeName = String(activity.name || "Unknown Place");
            
            // Handle location - it might be a string or an object with address/coordinates
            let safeLocation: string | null = null;
            if (activity.location) {
              if (typeof activity.location === 'string') {
                safeLocation = activity.location;
              } else if (typeof activity.location === 'object') {
                // If it's an object, try to extract address or formatted_address
                const loc = activity.location as any;
                safeLocation = loc.address || loc.formatted_address || loc.name || null;
              }
            }

            return (
              <article
                key={activity.id || `place-${index}`}
                className="flex-none group snap-center"
                style={{ width: cardWidth }}
              >
                <div className="bg-white rounded-2xl overflow-hidden border-2 border-black hover:shadow-2xl transition-all duration-300 flex flex-col h-full">
                  <div className="relative h-36 sm:h-48 overflow-hidden bg-black flex-shrink-0">
                  {activity.image && !failedImages.has(activity.id) ? (
                    <img
                      src={activity.image}
                      alt={safeName}
                      className="w-full h-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => {
                        console.error("Image failed to load:", activity.image, e);
                        setFailedImages(prev => new Set(prev).add(activity.id));
                      }}
                      crossOrigin="anonymous"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                      {activity.icon ? (
                        <img src={activity.icon} alt="" className="w-12 h-12 opacity-80" />
                      ) : (
                        <span className="text-4xl">📍</span>
                      )}
                    </div>
                  )}
                    {hasOpenInfo && (
                      <div className={`absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-bold shadow-md ${
                        isOpen ? "bg-green-500 text-white" : "bg-red-500 text-white"
                      }`}>
                        {isOpen ? "Open" : "Closed"}
                      </div>
                    )}
                    {!isOperational && (
                      <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-bold bg-gray-700 text-white shadow-md">
                        Temporarily closed
                      </div>
                    )}
                  </div>

                  <div className="p-4 sm:p-5 flex flex-col flex-grow">
                    {primaryType && (
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide mb-2 uppercase">
                        {primaryType}
                      </p>
                    )}

                    <h2 className="text-black text-base sm:text-xl font-bold line-clamp-3 leading-tight mb-3">
                      {safeName}
                    </h2>

                    {safeLocation && (
                      <p className="text-gray-500 text-sm flex items-start gap-1 mb-3">
                        <span className="flex-shrink-0">📍</span>
                        <span className="line-clamp-2">{safeLocation}</span>
                      </p>
                    )}

                    {hasRating && (
                      <div className="flex items-center gap-2 mb-3">
                        <div className="flex items-center gap-1">
                          <span className="text-yellow-500 text-lg">★</span>
                          <span className="font-semibold">{activity.rating!.score!.toFixed(1)}</span>
                        </div>
                        {activity.rating!.reviews && (
                          <span className="text-sm text-gray-500">
                            ({activity.rating!.reviews.toLocaleString()} reviews)
                          </span>
                        )}
                      </div>
                    )}

                    <div className="mt-auto">
                      {activity.mapLink ? (
                        <button
                          onClick={() => {
                            if (activity.mapLink) {
                              window.openai?.openExternal?.({
                                href: activity.mapLink,
                              });
                            }
                          }}
                          className="w-full bg-black text-white font-semibold py-2.5 sm:py-3 text-sm sm:text-base rounded-xl transition-all duration-300 hover:bg-gray-800 text-center"
                        >
                          View on Map
                        </button>
                      ) : (
                        <div className="w-full py-2.5 bg-gray-300 text-gray-600 rounded-xl font-semibold text-center">
                          No map link
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
          </div>
        </div>
        )}
      </main>
    </div>
  );
}
